import { useEffect, useRef, useState } from 'react';
import { keyframes, styled } from '@mui/material/styles';

/**
 * A small burst around the "Add to Schedule" button.
 *
 * Deliberately restrained: twelve 4px pieces, 700ms, drawn from the tag palette
 * so it reads as part of the same visual system rather than party confetti.
 * Rendered in a `pointer-events: none` overlay so it never intercepts a click,
 * and skipped entirely under `prefers-reduced-motion`.
 *
 * Those are the defaults, sized for a button. Something larger, like the label a
 * phone swipe leaves behind, passes a longer reach and more pieces.
 */

const COLORS = ['#bfffb9', '#b9dcff', '#fff0b9', '#ffb9f1', '#ffcbb9', '#d0b9ff'];

const burst = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.4) rotate(0deg); opacity: 0; }
  18%  { opacity: 1; }
  100% {
    transform:
      translate(calc(-50% + var(--dx)), calc(-50% + var(--dy)))
      scale(0.85) rotate(var(--spin));
    opacity: 0;
  }
`;

const Field = styled('span')({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  overflow: 'visible',
  zIndex: 3,
});

const Piece = styled('i')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  borderRadius: 1,
  animationName: burst,
  animationTimingFunction: 'cubic-bezier(0.16, 0.8, 0.35, 1)',
  animationFillMode: 'forwards',
  '@media (prefers-reduced-motion: reduce)': { display: 'none' },
});

interface ConfettiProps {
  /** Increment to fire a burst. */
  trigger: number;
  pieces?: number;
  /** How far the nearest pieces fly, in px. The farthest go about 60% further. */
  reach?: number;
  /** Each piece's side, in px. */
  size?: number;
  durationMs?: number;
}

export default function Confetti({ trigger, pieces = 12, reach = 16, size = 4, durationMs = 700 }: ConfettiProps) {
  const [runId, setRunId] = useState(0);
  const first = useRef(true);

  useEffect(() => {
    // Don't fire on mount, only on subsequent changes.
    if (first.current) {
      first.current = false;
      return;
    }
    if (trigger <= 0) return;
    setRunId((n) => n + 1);
    const id = setTimeout(() => setRunId(0), durationMs);
    return () => clearTimeout(id);
  }, [trigger, durationMs]);

  if (runId === 0) return null;

  return (
    <Field aria-hidden="true">
      {Array.from({ length: pieces }, (_, i) => {
        // Even spread with a little jitter, so it doesn't read as a clock face.
        const angle = (i / pieces) * Math.PI * 2 + (i % 3) * 0.22;
        const distance = reach * (1 + ((i * 7) % 11) / 16);
        return (
          <Piece
            key={`${runId}-${i}`}
            style={
              {
                width: size,
                height: size,
                background: COLORS[i % COLORS.length],
                animationDuration: `${durationMs}ms`,
                animationDelay: `${(i % 4) * 18}ms`,
                '--dx': `${Math.cos(angle) * distance}px`,
                '--dy': `${Math.sin(angle) * distance}px`,
                '--spin': `${(i % 2 ? 1 : -1) * (120 + i * 15)}deg`,
              } as React.CSSProperties
            }
          />
        );
      })}
    </Field>
  );
}
